/**
 * profileValidation.ts — Shared range and enum validation for HealthProfile.
 *
 * This module is the SINGLE SOURCE OF TRUTH for validation ranges.
 * Both the Worker (PUT /me/profile) and the D15 profileParser import from here
 * to prevent drift between validation layers (design ADR-3, R3).
 *
 * PII-safe: validation errors include field name + reason ONLY — never the raw value.
 *
 * Design: Fase 6.1 ADR-3 — shared validation layer.
 */

import type { HealthProfile, Allergen, Intolerance, MedicalCondition, DietType } from "./models.js";
import type { DayActivityType, ActivityIntensity } from "./engine/types.js";

// ---------------------------------------------------------------------------
// Numeric range constants (single source of truth — used by Worker and front-end)
// ---------------------------------------------------------------------------

/**
 * Valid numeric ranges for HealthProfile fields.
 * Mirrors the ranges in profileParser.ts (D15) — those MUST import these instead
 * of defining them locally to avoid drift.
 */
export const PROFILE_RANGES = {
  weightKg: { min: 20, max: 400 },
  heightCm: { min: 80, max: 250 },
  ageYears: { min: 5, max: 120 },
  bodyFatPct: { min: 0, max: 60 },
  targetWeightKg: { min: 20, max: 400 },
  waistCm: { min: 30, max: 250 },
  hipCm: { min: 30, max: 250 },
  mealsPerDay: { min: 1, max: 10 },
} as const satisfies Record<string, { min: number; max: number }>;

// ---------------------------------------------------------------------------
// Enum value sets (typed; derived from the union types in models.ts)
// ---------------------------------------------------------------------------

/** Valid Allergen values — must stay in sync with the Allergen union in models.ts */
const VALID_ALLERGENS = new Set<Allergen>([
  "milk", "egg", "peanut", "tree_nuts", "soy", "wheat_gluten",
  "fish", "shellfish", "sesame", "sulphites", "celery", "mustard",
  "lupin", "molluscs", "other",
]);

/** Valid Intolerance values — must stay in sync with the Intolerance union in models.ts */
const VALID_INTOLERANCES = new Set<Intolerance>([
  "lactose", "fructose_sorbitol", "fodmaps", "histamine", "caffeine", "gluten_non_celiac",
]);

/** Valid MedicalCondition values — must stay in sync with the MedicalCondition union */
const VALID_CONDITIONS = new Set<MedicalCondition>([
  "diabetes_t1", "diabetes_t2", "prediabetes", "hypertension", "dyslipidemia",
  "ckd", "celiac", "ibs_ibd", "hypo_hyperthyroidism", "pcos_insulin_resistance",
  "nafld", "gout", "gerd_gastritis", "iron_deficiency_anemia", "pregnancy_lactation",
  "other",
]);

/** Valid DietType values — must stay in sync with the DietType union in models.ts */
const VALID_DIET_TYPES = new Set<DietType>([
  "omnivore", "vegetarian", "vegan", "pescatarian", "flexitarian",
]);

// ---------------------------------------------------------------------------
// Validation result types
// ---------------------------------------------------------------------------

export interface ValidationError {
  /** Field name (never the field value — PII-safe) */
  field: string;
  /** Human-readable reason for the failure (never includes the raw value) */
  reason: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

// ---------------------------------------------------------------------------
// validateHealthProfileRanges
// ---------------------------------------------------------------------------

/**
 * Validate numeric fields of a partial HealthProfile against PROFILE_RANGES.
 *
 * PII-safe: errors contain field name + range description only — NEVER the raw value.
 *
 * @param partial - Partial HealthProfile (any subset of fields may be present).
 * @returns { valid, errors } — valid=true when there are no range violations.
 */
export function validateHealthProfileRanges(
  partial: Partial<HealthProfile>,
): ValidationResult {
  const errors: ValidationError[] = [];

  for (const [field, range] of Object.entries(PROFILE_RANGES) as Array<
    [keyof typeof PROFILE_RANGES, { min: number; max: number }]
  >) {
    const rawValue = partial[field as keyof Partial<HealthProfile>];
    if (rawValue === undefined) continue;

    if (typeof rawValue !== "number" || !Number.isFinite(rawValue)) {
      errors.push({ field, reason: "must be a valid number" });
      continue;
    }

    if (rawValue < range.min || rawValue > range.max) {
      errors.push({
        field,
        reason: `must be between ${range.min} and ${range.max}`,
      });
    }
  }

  return { valid: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// validateEnums
// ---------------------------------------------------------------------------

/**
 * Validate enum array fields of a partial HealthProfile.
 * Checks: allergies, intolerances, conditions, dietType.
 *
 * PII-safe: errors contain field name only — NEVER the invalid value.
 *
 * @param partial - Partial HealthProfile (any subset of fields may be present).
 * @returns { valid, errors } — valid=true when all enum values are recognized.
 */
export function validateEnums(
  partial: Partial<HealthProfile>,
): ValidationResult {
  const errors: ValidationError[] = [];

  // allergies: Allergen[]
  if (partial.allergies !== undefined) {
    const invalid = partial.allergies.filter(
      (v) => !VALID_ALLERGENS.has(v as Allergen),
    );
    if (invalid.length > 0) {
      errors.push({
        field: "allergies",
        reason: `contains unrecognized allergen value(s) — must be one of: ${[...VALID_ALLERGENS].join(", ")}`,
      });
    }
  }

  // intolerances: Intolerance[]
  if (partial.intolerances !== undefined) {
    const invalid = partial.intolerances.filter(
      (v) => !VALID_INTOLERANCES.has(v as Intolerance),
    );
    if (invalid.length > 0) {
      errors.push({
        field: "intolerances",
        reason: `contains unrecognized intolerance value(s) — must be one of: ${[...VALID_INTOLERANCES].join(", ")}`,
      });
    }
  }

  // conditions: MedicalCondition[]
  if (partial.conditions !== undefined) {
    const invalid = partial.conditions.filter(
      (v) => !VALID_CONDITIONS.has(v as MedicalCondition),
    );
    if (invalid.length > 0) {
      errors.push({
        field: "conditions",
        reason: `contains unrecognized condition value(s) — must be one of: ${[...VALID_CONDITIONS].join(", ")}`,
      });
    }
  }

  // dietType: DietType (single value, not array)
  if (partial.dietType !== undefined) {
    if (!VALID_DIET_TYPES.has(partial.dietType as DietType)) {
      errors.push({
        field: "dietType",
        reason: `unrecognized diet type — must be one of: ${[...VALID_DIET_TYPES].join(", ")}`,
      });
    }
  }

  return { valid: errors.length === 0, errors };
}

// ---------------------------------------------------------------------------
// validateWeeklyActivity — Fase 7
// ---------------------------------------------------------------------------

/** Valid DayActivityType values — must stay in sync with DayActivityType in engine/types.ts */
const VALID_ACTIVITY_TYPES = new Set<DayActivityType>([
  "sleep", "work_sedentary", "work_active", "study", "training",
]);

/** Valid ActivityIntensity values — must stay in sync with ActivityIntensity in engine/types.ts */
const VALID_INTENSITIES = new Set<ActivityIntensity>([
  "light", "moderate", "hard", "very_hard",
]);

/**
 * Validate a weeklyActivity payload from PUT /me/profile.
 *
 * Rules (spec weekly-activity-profile §weeklyActivity Validation):
 *   - Each activity durationHours must be > 0 and <= 24.
 *   - type === "training" requires a valid intensity value.
 *   - Sum of durationHours per weekday must not exceed 24.
 *   - weekday must be 0–6.
 *   - activity type must be a valid DayActivityType.
 *
 * PII-safe: errors contain field name + reason — NEVER raw values.
 *
 * @param weeklyActivity - Raw incoming array (not yet typed — validated here).
 * @returns { valid, errors }
 */
export function validateWeeklyActivity(
  weeklyActivity: unknown,
): ValidationResult {
  const errors: ValidationError[] = [];

  if (!Array.isArray(weeklyActivity)) {
    errors.push({ field: "weeklyActivity", reason: "must be an array" });
    return { valid: false, errors };
  }

  for (let i = 0; i < weeklyActivity.length; i++) {
    const entry = weeklyActivity[i] as Record<string, unknown>;

    if (typeof entry !== "object" || entry === null) {
      errors.push({ field: `weeklyActivity[${i}]`, reason: "must be an object" });
      continue;
    }

    const weekday = entry["weekday"];
    if (typeof weekday !== "number" || !Number.isInteger(weekday) || weekday < 0 || weekday > 6) {
      errors.push({ field: `weeklyActivity[${i}].weekday`, reason: "must be an integer 0–6" });
    }

    const activities = entry["activities"];
    if (!Array.isArray(activities)) {
      errors.push({ field: `weeklyActivity[${i}].activities`, reason: "must be an array" });
      continue;
    }

    let totalHours = 0;

    for (let j = 0; j < activities.length; j++) {
      const act = activities[j] as Record<string, unknown>;

      if (typeof act !== "object" || act === null) {
        errors.push({ field: `weeklyActivity[${i}].activities[${j}]`, reason: "must be an object" });
        continue;
      }

      const type = act["type"];
      if (typeof type !== "string" || !VALID_ACTIVITY_TYPES.has(type as DayActivityType)) {
        errors.push({
          field: `weeklyActivity[${i}].activities[${j}].type`,
          reason: `must be one of: ${[...VALID_ACTIVITY_TYPES].join(", ")}`,
        });
      }

      const durationHours = act["durationHours"];
      if (
        typeof durationHours !== "number" ||
        !Number.isFinite(durationHours) ||
        durationHours <= 0
      ) {
        errors.push({
          field: `weeklyActivity[${i}].activities[${j}].durationHours`,
          reason: "must be a positive number",
        });
      } else {
        totalHours += durationHours;
      }

      // intensity is required when type === "training"
      if (type === "training") {
        const intensity = act["intensity"];
        if (typeof intensity !== "string" || !VALID_INTENSITIES.has(intensity as ActivityIntensity)) {
          errors.push({
            field: `weeklyActivity[${i}].activities[${j}].intensity`,
            reason: `required when type is "training"; must be one of: ${[...VALID_INTENSITIES].join(", ")}`,
          });
        }
      }
    }

    if (totalHours > 24) {
      errors.push({
        field: `weeklyActivity[${i}].weekday`,
        reason: "sum of activity durationHours must not exceed 24",
      });
    }
  }

  return { valid: errors.length === 0, errors };
}
